"use client";
import { getApiBase, getApiOrigin } from "@/lib/apiBase";

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Loader2,
  UserPlus,
  Mail,
  Smartphone,
  List as ListIcon,
  PlusCircle,
  Download,
  Edit,
  Trash2,
  Save,
  X,
  User,
  Phone,
  MapPin,
  Building,
  Monitor,
  UserCheck,
  AlertCircle,
  Search,
  Unlock,
  Lock,
  Eye,
  ArrowLeft,
  Calendar,
  LayoutGrid,
  BookOpen,
  ClipboardCheck,
  Video,
  PlayCircle,
  Film,
  Newspaper,
  Quote,
  Wallet,
  FileText,
  Headphones,
  Award,
} from "lucide-react";
import CourseTab from "../components/CourseTab";
import RevisionTab from "../components/RevisionTab.tsx";
import LiveClassTab from "../components/LiveClassTab";
import LiveClassListTab from "../components/LiveClassListTab";
import RecordedSessionTab from "../components/RecordedSessionTab";
import NewsTab from "../components/NewsTab";
import TestimonialTab from "../components/TestimonialTab";
import StudentInvoicesTab from "../components/StudentInvoicesTab";
import StudentBookingsTab from "../components/StudentBookingsTab";
import TicketTab from "../components/TicketTab";
import CertificateTab from "../components/CertificateTab";
import { FaWhatsapp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';

const API_BASE = getApiBase();
const API_ORIGIN = getApiOrigin();
const isStudentSuspended = (student) => {
  const status = String(student?.status || "").toLowerCase();
  return status === "inactive" || status === "suspended";
};

const extractStudents = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.data?.students)) return payload.data.students;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// BASIC COMPONENTS
const Button = ({ className = "", children, ...props }) => (
  <button
    className={
      "px-4 py-2 rounded-xl font-semibold focus:outline-none transition-all " +
      className
    }
    {...props}
  >
    {children}
  </button>
);

const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={
      "peer block w-full p-4 pt-6 rounded-xl border border-indigo-200 bg-white focus:ring-2 focus:ring-indigo-500 transition-all " +
      className
    }
    {...props}
  />
));
Input.displayName = "Input";

const Label = ({ htmlFor, floating, active, className = "", children }) => (
  <label
    htmlFor={htmlFor}
    className={`
      absolute left-4 bg-white px-1 rounded pointer-events-none transition-all
      text-gray-500
      ${
        floating
          ? "peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs"
          : ""
      }
      ${active ? "-top-2 text-xs" : "top-4 text-base"}
      ${className}
    `}
  >
    {children}
  </label>
);

const getWhatsAppLink = (phone) => `https://wa.me/${phone.replace(/\D/g, "")}`;

const formatDisplayDate = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString("en-IN");
};

const getCourseId = (course, seen = new Set()) => {
  if (!course) return "";
  if (typeof course === "string" || typeof course === "number" || typeof course === "bigint") {
    return String(course);
  }
  if (typeof course !== "object") return "";
  if (seen.has(course)) return "";
  seen.add(course);

  if (typeof course.toHexString === "function") {
    const hexString = course.toHexString();
    if (hexString) return hexString;
  }

  if (typeof course.toString === "function") {
    const stringValue = course.toString();
    if (stringValue && stringValue !== "[object Object]") {
      return stringValue;
    }
  }

  const candidates = [course.courseId, course._id, course.id];
  for (const candidate of candidates) {
    if (candidate == null || seen.has(candidate)) continue;
    const normalized = getCourseId(candidate, seen);
    if (normalized) return normalized;
  }

  return "";
};

const getCourseTitle = (course) =>
  course?.title || course?.name || (typeof course === "string" ? course : "Untitled Course");

const getCourseAccessState = (course) => {
  const isLocked = course?.isLocked === true;
  const isExpired = Boolean(course?.isExpired);

  return {
    isLocked,
    isExpired,
    status: isLocked || isExpired ? "Inactive" : "Active",
  };
};

const normalizeBatchModeValue = (value) =>
  String(value || "").trim().toLowerCase();

const getFetchErrorMessage = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "";
  const networkFailure =
    error?.message === "Network Error" ||
    error?.code === "ERR_NETWORK" ||
    !error?.response;

  if (networkFailure && !status) {
    return "Network error. Please check your connection and try again.";
  }

  if (status === 401) {
    return `${responseMessage || "Unauthorized"} (HTTP 401)`;
  }

  if (status === 403) {
    return `${responseMessage || "Forbidden"} (HTTP 403)`;
  }

  if (status >= 500) {
    return `${responseMessage || "Server error"} (HTTP ${status})`;
  }

  const message = responseMessage || error?.message || fallbackMessage;
  return status ? `${message} (HTTP ${status})` : message;
};

const mergeCourseAccessUpdate = (course, override = {}) => {
  const nextIsLocked =
    typeof override?.isLocked === "boolean"
      ? override.isLocked
      : Boolean(course?.isLocked);
  const nextIsExpired =
    typeof override?.isExpired === "boolean"
      ? override.isExpired
      : Boolean(course?.isExpired);

  return {
    ...course,
    ...override,
    isLocked: nextIsLocked,
    isExpired: nextIsExpired,
    status: nextIsLocked || nextIsExpired ? "Inactive" : "Active",
  };
};

const mergeCourseAccessOverrides = (overrides, courseId, override = {}) => {
  const normalizedCourseId = String(courseId || "");
  if (!normalizedCourseId) return Array.isArray(overrides) ? overrides : [];

  const nextOverride = {
    ...override,
    courseId: normalizedCourseId,
    isLocked:
      typeof override?.isLocked === "boolean"
        ? override.isLocked
        : Boolean(override?.locked ?? override?.is_locked),
  };

  const existingOverrides = Array.isArray(overrides) ? overrides : [];
  let updated = false;

  const nextOverrides = existingOverrides.map((entry) => {
    const entryCourseId = getCourseId(entry);
    if (String(entryCourseId) !== normalizedCourseId) {
      return entry;
    }

    updated = true;
    return {
      ...entry,
      ...nextOverride,
      courseId: normalizedCourseId,
    };
  });

  if (!updated) {
    nextOverrides.push(nextOverride);
  }

  return nextOverrides;
};

const mergeStudentCourseAccess = (student, courseId, override = {}) => {
  if (!student) return student;

  const nextCourses = Array.isArray(student.course)
    ? student.course.map((course) => {
        const currentCourseId = getCourseId(course);
        if (String(currentCourseId) !== String(courseId)) {
          return course;
        }
        return mergeCourseAccessUpdate(course, override);
      })
    : student.course;

  return {
    ...student,
    course: nextCourses,
    courseAccessOverrides: mergeCourseAccessOverrides(
      student.courseAccessOverrides,
      courseId,
      override
    ),
  };
};

const setPendingCourseAccessOverride = (pendingStore, studentId, courseId, override) => {
  const studentKey = String(studentId || "");
  const courseKey = String(courseId || "");
  if (!studentKey || !courseKey || !override) return;

  let studentOverrides = pendingStore.get(studentKey);
  if (!studentOverrides) {
    studentOverrides = new Map();
    pendingStore.set(studentKey, studentOverrides);
  }

  studentOverrides.set(courseKey, {
    ...override,
    courseId: courseKey,
  });
};

const reconcileStudentWithPendingCourseAccess = (student, pendingStore) => {
  if (!student || !pendingStore?.size) {
    return { student, remaining: new Map() };
  }

  const studentKey = String(student._id || "");
  const studentOverrides = pendingStore.get(studentKey);
  if (!studentKey || !studentOverrides?.size) {
    return { student, remaining: new Map() };
  }

  let nextStudent = student;
  const remaining = new Map();

  studentOverrides.forEach((override, courseId) => {
    const currentCourse = Array.isArray(nextStudent.course)
      ? nextStudent.course.find((course) => String(getCourseId(course)) === String(courseId))
      : null;

    if (currentCourse && currentCourse.isLocked === override.isLocked) {
      return;
    }

    nextStudent = mergeStudentCourseAccess(nextStudent, courseId, override);
    remaining.set(String(courseId), override);
  });

  return { student: nextStudent, remaining };
};

const reconcileStudentsWithPendingCourseAccess = (students, pendingStore) => {
  const nextPending = new Map();

  const nextStudents = (Array.isArray(students) ? students : []).map((student) => {
    const { student: reconciledStudent, remaining } = reconcileStudentWithPendingCourseAccess(
      student,
      pendingStore
    );

    if (remaining.size > 0) {
      nextPending.set(String(student._id), remaining);
    }

    return reconciledStudent;
  });

  (pendingStore instanceof Map ? pendingStore : new Map()).forEach((studentOverrides, studentId) => {
    if (nextPending.has(String(studentId))) return;
    if (studentOverrides?.size > 0) {
      nextPending.set(String(studentId), new Map(studentOverrides));
    }
  });

  return { students: nextStudents, pending: nextPending };
};

const reconcileOverviewWithPendingCourseAccess = (data, pendingStore) => {
  if (!data || !(pendingStore instanceof Map) || pendingStore.size === 0) {
    return { data, pending: pendingStore instanceof Map ? new Map(pendingStore) : new Map() };
  }

  const studentKey = String(data?.student?._id || "");
  const studentOverrides = pendingStore.get(studentKey);
  if (!studentKey || !studentOverrides?.size) {
    return { data, pending: new Map(pendingStore) };
  }

  let nextData = data;
  const remaining = new Map();

  studentOverrides.forEach((override, courseId) => {
    const courseInOverview = Array.isArray(nextData.courses)
      ? nextData.courses.find(
          (course) => String(course?.courseId || course?._id) === String(courseId)
        )
      : null;

    if (courseInOverview && courseInOverview.isLocked === override.isLocked) {
      return;
    }

    nextData = {
      ...nextData,
      student: nextData.student
        ? mergeStudentCourseAccess(nextData.student, courseId, override)
        : nextData.student,
      courses: Array.isArray(nextData.courses)
        ? nextData.courses.map((course) => {
            const currentCourseId = course?.courseId || course?._id;
            return String(currentCourseId) === String(courseId)
              ? mergeCourseAccessUpdate(course, override)
              : course;
          })
        : nextData.courses,
    };
    remaining.set(String(courseId), override);
  });

  const nextPending = new Map(pendingStore);
  if (remaining.size > 0) {
    nextPending.set(studentKey, remaining);
  } else {
    nextPending.delete(studentKey);
  }

  return { data: nextData, pending: nextPending };
};

const initialState = {
  name: "",
  email: "",
  phone: "",
  password: "",
  location: "",
  course: "",
  teacher: "",
};

// Edit Student Modal Component
function EditStudentModal({ student, isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    mode: "",
    location: "",
    center: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Initialize form when student changes
  useEffect(() => {
    if (student) {
      setForm({
        name: student.name || "",
        email: student.email || "",
        phone: student.phone || "",
        mode: student.mode || "",
        location: student.location || "",
        center: student.center || "",
        password: "", // Don't pre-fill password
      });
    }
  }, [student]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("No admin token found");
      }

      // Prepare update data (exclude empty password)
      const updateData = { ...form };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await axios.put(
        `${API_BASE}/v1/students/admin/update-profile/${student._id}`,
        updateData,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      toast.success("Student profile updated successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating student:", err);
      setError(getFetchErrorMessage(err, "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Edit className="text-indigo-500" size={24} />
              <h2 className="text-2xl font-bold text-gray-800">
                Edit Student Profile
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: "Full Name", name: "name", type: "text", required: true },
                { label: "Email Address", name: "email", type: "email", required: true },
                { label: "Phone Number", name: "phone", type: "tel", required: true },
                { label: "Mode", name: "mode", type: "select", options: ["online", "offline"] },
                { label: "Location", name: "location", type: "text" },
                { label: "Center", name: "center", type: "text" },
                { label: "New Password (optional)", name: "password", type: "password" },
              ].map((field) => (
                <div key={field.name} className="relative">
                  {field.type === "select" ? (
                    <select
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      required={field.required}
                      className="peer block w-full p-4 pt-6 rounded-xl border border-indigo-200 bg-white focus:ring-2 focus:ring-indigo-500 transition-all"
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      name={field.name}
                      type={field.type}
                      placeholder=" "
                      required={field.required}
                      value={form[field.name]}
                      onChange={handleChange}
                      disabled={loading}
                      className="w-full"
                    />
                  )}
                  <Label
                    htmlFor={field.name}
                    floating
                    active={!!form[field.name]}
                    className="text-gray-500"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Note about profile image */}
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
              <p className="text-sm">
                <strong>Note:</strong> Profile image cannot be changed through this interface. 
                Students can update their profile image from their dashboard.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-500 hover:bg-indigo-600 text-white flex items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {loading ? "Updating..." : "Update Profile"}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function AddStudentForm({ onSuccess }) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        `${API_BASE}/v1/students/register`,
        form,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      setSuccess("Student registered successfully!");
      setForm(initialState);
      if (onSuccess) onSuccess(res.data.student);
    } catch (err) {
      setError(getFetchErrorMessage(err, "Something went wrong"));
    }
    setLoading(false);
  };

  return (
    <motion.div
      key="add"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="w-full"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center space-x-3 mb-6">
          <UserPlus className="text-indigo-500" size={28} />
          <h2 className="text-2xl font-bold tracking-tight text-gray-800">Add New Student</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { label: "Full Name", name: "name", type: "text" },
              { label: "Email Address", name: "email", type: "email" },
              { label: "Phone Number", name: "phone", type: "tel" },
              { label: "Password", name: "password", type: "password" },
              { label: "Location", name: "location", type: "text" },
              { label: "Course", name: "course", type: "text" },
              { label: "Teacher", name: "teacher", type: "text" },
            ].map((field) => (
              <div key={field.name} className="relative">
                <Input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  placeholder=" "
                  required
                  autoComplete="off"
                  value={form[field.name]}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full"
                />
                <Label
                  htmlFor={field.name}
                  floating
                  active={!!form[field.name]}
                >
                  {field.label}
                </Label>
              </div>
            ))}
          </div>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-sm rounded-lg p-4 bg-red-50 border border-red-200"
            >
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-green-600 text-sm rounded-lg p-4 bg-green-50 border border-green-200"
            >
              {success}
            </motion.div>
          )}
          
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              className="px-8 py-3 rounded-xl text-lg font-semibold gap-2 bg-gradient-to-r from-indigo-500 to-sky-400 hover:from-indigo-600 hover:to-blue-500 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={20} />
                  Adding Student...
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Add Student
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}

function StudentsTable({
  students,
  onStudentUpdated,
  onViewStudent,
  onPreviewCourses,
  onCourseAccessUpdated,
  onQueueCourseAccessUpdate,
  errorMessage = "",
}) {
  const [editingStudent, setEditingStudent] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [updatingCourseId, setUpdatingCourseId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [liveClassUpdatingId, setLiveClassUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all"); // "all", "paid", "unpaid"

  const handleSendScheduleEmail = async (student) => {
    const studentBatchId = student.batchId?._id || student.batchId;
    if (!studentBatchId) {
      toast.error("Student is not assigned to any batch");
      return;
    }

    try {
      setSendingEmailId(student._id);
      const token = localStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${API_BASE}/batch-manager/${studentBatchId}/send-schedule/${student._id}`,
        {},
        { headers }
      );

      toast.success(response.data?.message || "Schedule email sent successfully!");
    } catch (error) {
      console.error("Error sending schedule email:", error);
      toast.error(
        getFetchErrorMessage(error, "Failed to send schedule email")
      );
    } finally {
      setSendingEmailId(null);
    }
  };

  const getPaymentStatus = (student) => {
    return student.course && student.course.length > 0 ? "Paid" : "Unpaid";
  };

  const filteredStudents = (students || []).filter((student) => {
    const status = getPaymentStatus(student).toLowerCase();
    const matchesFilter = paymentFilter === "all" || status === paymentFilter;
    
    const searchStr = searchTerm.toLowerCase();
    const matchesSearch = 
      student.name?.toLowerCase().includes(searchStr) ||
      student.email?.toLowerCase().includes(searchStr) ||
      student.phone?.includes(searchStr) ||
      student.location?.toLowerCase().includes(searchStr);
      
    return matchesFilter && matchesSearch;
  });

  if (!students?.length)
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center text-gray-500">
        {errorMessage ? (
          <div className="flex flex-col items-center gap-3 text-rose-700">
            <AlertCircle size={24} />
            <p className="max-w-xl">{errorMessage}</p>
          </div>
        ) : (
          "No students registered yet."
        )}
      </div>
    );

  // Handle edit student
  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingStudent(null);
  };

  const handleEditSuccess = () => {
    onStudentUpdated();
  };

  // Handle delete student
  const handleDeleteStudent = async (student) => {
    if (window.confirm(`Are you sure you want to delete ${student.name}? This action cannot be undone.`)) {
      try {
        console.log("Deleting student with ID:", student._id);
        console.log("API Base:", API_BASE);
        console.log("Full URL:", `${API_BASE}/v1/students/${student._id}`);

        const response = await axios.delete(`${API_BASE}/v1/students/${student._id}`);
        console.log("Delete response:", response.data);
        
        toast.success(`${student.name} has been deleted successfully!`);
        if (onStudentUpdated) onStudentUpdated();
      } catch (error) {
        console.error("Error deleting student:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          studentId: student._id
        });
        toast.error(getFetchErrorMessage(error, "Failed to delete student"));
      }
    }
  };

  const handleToggleCourseAccess = async (student, course) => {
    const courseId = getCourseId(course);
    if (!student?._id || !courseId) return;

    try {
      setUpdatingCourseId(`${student._id}:${courseId}`);
      const token = localStorage.getItem("adminToken");
      const response = await axios.put(
        `${API_BASE}/v1/students/admin/course-access/${student._id}/${courseId}`,
        { locked: !Boolean(course?.isLocked) },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      toast.success(response.data?.message || "Course access updated");
      if (response.data?.override) {
        onQueueCourseAccessUpdate?.(student._id, courseId, response.data.override);
        onCourseAccessUpdated?.(student._id, courseId, response.data.override);
      }
      onStudentUpdated?.();
    } catch (error) {
      console.error("Error updating course access:", error);
      toast.error(
        getFetchErrorMessage(error, "Failed to update course access")
      );
    } finally {
      setUpdatingCourseId(null);
    }
  };

  // Handle suspend student
  const handleSuspendStudent = async (student) => {
    const isCurrentlySuspended = isStudentSuspended(student);
    const action = isCurrentlySuspended ? "unsuspend" : "suspend";
    const nextStatus = isCurrentlySuspended ? "active" : "inactive";
    
    if (window.confirm(`Are you sure you want to ${action} ${student.name}?`)) {
      try {
        setStatusUpdatingId(student._id);
        await axios.put(`${API_BASE}/v1/students/${student._id}/status`, {
          status: nextStatus
        });
        toast.success(
          `${student.name} has been ${isCurrentlySuspended ? "unsuspended" : "suspended"} successfully!`
        );
        onStudentUpdated?.();
      } catch (error) {
        console.error(`Error ${action}ing student:`, error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          studentId: student._id
        });
        toast.error(getFetchErrorMessage(error, `Failed to ${action} student`));
      } finally {
        setStatusUpdatingId(null);
      }
    }
  };

  const handleToggleLiveClassAccess = async (student) => {
    const nextEnabled = student.liveClassAccessEnabled !== false ? false : true;
    try {
      setLiveClassUpdatingId(student._id);
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API_BASE}/v1/students/admin/live-class-access/${student._id}`,
        { enabled: nextEnabled },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(
        nextEnabled
          ? `Live Class enabled for ${student.name}`
          : `Live Class disabled for ${student.name}`
      );
      onStudentUpdated?.();
    } catch (error) {
      toast.error(getFetchErrorMessage(error, "Failed to update Live Class access"));
    } finally {
      setLiveClassUpdatingId(null);
    }
  };

  // Export students to Excel
  const exportToExcel = () => {
    if (students.length === 0) {
      toast.error("No students to export");
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = filteredStudents.map((student, index) => ({
        'S.No': index + 1,
        'Name': student.name || '',
        'Email': student.email || '',
        'Phone': student.phone || '',
        'Location': student.location || '',
        'Center': student.center || '',
        'Batch': student.batchCode || student.batchId?.code || '',
        'Payment Status': getPaymentStatus(student),
        'Status': student.mode || 'Not specified',
        'Courses': student.course && student.course.length > 0 
          ? student.course.map(course => course.title || course).join(', ') 
          : 'No courses',
        'Live Sessions': student.enrolledLiveSessions && student.enrolledLiveSessions.length > 0 
          ? `${student.enrolledLiveSessions.length} session(s)` 
          : 'No sessions',
        'Registered Date': student.createdAt ? new Date(student.createdAt).toLocaleDateString() : '',
        'Registered Time': student.createdAt ? new Date(student.createdAt).toLocaleTimeString() : '',
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 20 },  // Name
        { wch: 30 },  // Email
        { wch: 15 },  // Phone
        { wch: 20 },  // Location
        { wch: 20 },  // Center
        { wch: 16 },  // Batch
        { wch: 12 },  // Status
        { wch: 40 },  // Courses
        { wch: 20 },  // Live Sessions
        { wch: 15 },  // Registered Date
        { wch: 15 },  // Registered Time
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Students');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `students_export_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast.success(`${students.length} students exported to Excel successfully!`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export students to Excel");
    }
  };

  return (
    <motion.div
      key="list"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className="w-full"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        {errorMessage && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div>{errorMessage}</div>
          </div>
        )}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Registered Students
            </h2>
            <p className="text-gray-600 mt-1">
              Total: {filteredStudents.length} students {paymentFilter !== 'all' ? `(${paymentFilter})` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm transition-all"
              />
            </div>
            
            {/* Payment Filter */}
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white cursor-pointer"
            >
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>

            <Button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm"
            >
              <Download size={16} />
              Export Excel
            </Button>
          </div>
        </div>
        
        {/* Students Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Suspend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Live Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0 mr-3">
                        {student.name?.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {student.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {student.location}
                      {student.center && (
                        <div className="text-xs text-gray-500">{student.center}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {student.batchCode || student.batchId?.code ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200">
                        {student.batchCode || student.batchId?.code}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">Not assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                      getPaymentStatus(student) === 'Paid'
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {getPaymentStatus(student)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      student.mode === 'online' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {student.mode || 'Not specified'}
                    </span>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <div className="text-sm text-gray-900">
                      {student.course && student.course.length > 0 ? (
                        <div className="space-y-2 min-w-[360px]">
                          {student.course.map((course, index) => {
                            const courseId = getCourseId(course) || `${student._id}-${index}`;
                            const accessState = getCourseAccessState(course);
                            const isLocked = accessState.isLocked;
                            const statusLabel = accessState.status;
                            const isUpdating = updatingCourseId === `${student._id}:${courseId}`;

                            return (
                              <div
                                key={courseId}
                                className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-semibold text-gray-900">
                                      {getCourseTitle(course)}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
                                      <span>
                                        <span className="font-medium text-gray-700">Purchased:</span>{" "}
                                        {formatDisplayDate(course?.purchasedAt)}
                                      </span>
                                      <span className="hidden sm:inline text-gray-300">|</span>
                                      <span>
                                        <span className="font-medium text-gray-700">Expiry:</span>{" "}
                                        {formatDisplayDate(course?.expiresAt)}
                                      </span>
                                      <span
                                        className={`inline-flex px-2 py-0.5 rounded-full font-semibold ${
                                          statusLabel === "Active"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                        }`}
                                      >
                                        {statusLabel}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={isLocked}
                                      aria-label={isLocked ? "Unlock course" : "Lock course"}
                                      onClick={() => handleToggleCourseAccess(student, course)}
                                      disabled={isUpdating}
                                      title={isLocked ? "Unlock course" : "Lock course"}
                                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        isLocked ? "bg-red-500" : "bg-green-500"
                                      } ${isUpdating ? "opacity-60 cursor-not-allowed" : ""}`}
                                    >
                                      <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                          isLocked ? "translate-x-6" : "translate-x-1"
                                        }`}
                                      />
                                    </button>
                                    <span
                                      className={`text-xs font-semibold ${
                                        isLocked ? "text-red-600" : "text-green-600"
                                      }`}
                                    >
                                      {isUpdating
                                        ? "Updating..."
                                        : isLocked
                                        ? "Unlock"
                                        : "Lock"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-500">No courses</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(student.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const isSuspended = isStudentSuspended(student);
                      const isUpdating = statusUpdatingId === student._id;

                      return (
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isSuspended}
                            onClick={() => handleSuspendStudent(student)}
                            disabled={isUpdating}
                            title={isSuspended ? "Unsuspend Student" : "Suspend Student"}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              isSuspended ? "bg-red-500" : "bg-green-500"
                            } ${isUpdating ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isSuspended ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                          <span
                            className={`text-xs font-semibold ${
                              isSuspended ? "text-red-600" : "text-green-600"
                            }`}
                          >
                            {isUpdating ? "Updating..." : isSuspended ? "Suspended" : "Active"}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const liveEnabled = student.liveClassAccessEnabled !== false;
                      const isUpdating = liveClassUpdatingId === student._id;
                      return (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={liveEnabled}
                            onClick={() => handleToggleLiveClassAccess(student)}
                            disabled={isUpdating}
                            title={liveEnabled ? "Disable Live Class" : "Enable Live Class"}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              liveEnabled ? "bg-green-500" : "bg-red-500"
                            } ${isUpdating ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                liveEnabled ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                          <span className={`text-xs font-semibold ${liveEnabled ? "text-green-600" : "text-red-600"}`}>
                            {isUpdating ? "..." : liveEnabled ? "On" : "Off"}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-1">
                      {/* Contact Actions */}
                      <a
                        href={`mailto:${student.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Send Email"
                        className="bg-indigo-500 hover:bg-indigo-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <Mail size={14} />
                      </a>
                      {(student.batchId?._id || student.batchId) && (
                        <button
                          onClick={() => handleSendScheduleEmail(student)}
                          disabled={sendingEmailId === student._id}
                          title="Send Batch Schedule Email"
                          className="bg-teal-600 hover:bg-teal-700 text-white p-2 rounded transition-colors flex items-center justify-center disabled:opacity-50"
                        >
                          {sendingEmailId === student._id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Calendar size={14} />
                          )}
                        </button>
                      )}
                      <a
                        href={getWhatsAppLink(student.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp"
                        className="bg-green-500 hover:bg-green-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <FaWhatsapp size={14} />
                      </a>
                      <a
                        href={`tel:${student.phone}`}
                        title="Call"
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <Smartphone size={14} />
                      </a>
                      
                      {/* Management Actions */}
                      <button
                        onClick={() => onViewStudent(student)}
                        title="View Student Details"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => onPreviewCourses(student)}
                        title="Preview Courses (Student View)"
                        className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <LayoutGrid size={14} />
                      </button>
                      <button
                        onClick={() => handleEditStudent(student)}
                        title="Edit Student"
                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(student)}
                        title="Delete Student"
                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded transition-colors flex items-center justify-center"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Student Modal */}
      <EditStudentModal
        student={editingStudent}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
      />
    </motion.div>
  );
}

function StudentDetailsView({
  studentId,
  onBack,
  onCourseAccessUpdated,
  onQueueCourseAccessUpdate,
  pendingCourseAccessOverridesRef,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [updatingCourseId, setUpdatingCourseId] = useState(null);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);
  const [batchError, setBatchError] = useState("");

  const refreshOverview = useCallback(
    async ({ background = false } = {}) => {
      if (!studentId) return;

      try {
        if (!background) {
          setLoading(true);
        }

        const token = localStorage.getItem("adminToken");
        const response = await axios.get(
          `${API_BASE}/v1/students/admin/${studentId}/overview`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        const reconciled = reconcileOverviewWithPendingCourseAccess(
          response.data || null,
          pendingCourseAccessOverridesRef?.current || new Map()
        );
        if (pendingCourseAccessOverridesRef?.current) {
          pendingCourseAccessOverridesRef.current = reconciled.pending;
        }
        setData(reconciled.data || null);
        setErrorMessage("");
      } catch (error) {
        console.error("Error fetching student details:", error);
        const message = getFetchErrorMessage(
          error,
          "Failed to fetch student details"
        );
        setErrorMessage(message);
        toast.error(message);
        setData(null);
      } finally {
        if (!background) {
          setLoading(false);
        }
      }
    },
    [studentId, pendingCourseAccessOverridesRef]
  );

  useEffect(() => {
    refreshOverview();
  }, [refreshOverview]);

  const student = data?.student || null;
  const studentBatchId = String(student?.batchId?._id || student?.batchId || "");
  const studentBatchMode = normalizeBatchModeValue(student?.batchMode || student?.mode);

  useEffect(() => {
    setSelectedBatchId(studentBatchId);
  }, [studentBatchId]);

  const refreshBatchOptions = useCallback(async () => {
    if (!student?._id) return;

    try {
      setBatchLoading(true);
      setBatchError("");

      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API_BASE}/batch-manager`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const allBatches = Array.isArray(response.data?.batches)
        ? response.data.batches
        : [];
      const filteredBatches = studentBatchMode
        ? allBatches.filter(
            (batch) => normalizeBatchModeValue(batch.mode) === studentBatchMode
          )
        : allBatches;
      const currentBatchFromList = studentBatchId
        ? allBatches.find((batch) => String(batch._id) === studentBatchId)
        : null;
      const currentBatch =
        currentBatchFromList ||
        (student?.batchId && typeof student.batchId === "object"
          ? student.batchId
          : null);
      const nextOptions =
        studentBatchId &&
        currentBatch &&
        !filteredBatches.some((batch) => String(batch._id) === studentBatchId)
          ? [currentBatch, ...filteredBatches]
          : filteredBatches;

      setBatchOptions(nextOptions);
      setSelectedBatchId(studentBatchId);
    } catch (error) {
      console.error("Error fetching batch options:", error);
      const message = getFetchErrorMessage(error, "Failed to load batch options");
      setBatchError(message);
      toast.error(message);
      setBatchOptions([]);
    } finally {
      setBatchLoading(false);
    }
  }, [student?._id, studentBatchId, studentBatchMode]);

  useEffect(() => {
    refreshBatchOptions();
  }, [refreshBatchOptions]);

  const formatDate = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleDateString("en-IN");
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleString("en-IN");
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });

  const handleBatchSave = async () => {
    if (!studentId || !selectedBatchId) {
      toast.error("Please choose a batch first");
      return;
    }

    if (String(selectedBatchId) === studentBatchId) {
      toast("Student is already assigned to this batch");
      return;
    }

    try {
      setSavingBatch(true);
      setBatchError("");

      const token = localStorage.getItem("adminToken");
      const response = await axios.put(
        `${API_BASE}/v1/students/admin/batch/${studentId}`,
        { batchId: selectedBatchId },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      toast.success(response.data?.message || "Student batch updated");
      if (response.data?.student) {
        setData((current) =>
          current
            ? {
                ...current,
                student: response.data.student,
              }
            : current
        );
      }
      await refreshOverview({ background: true });
    } catch (error) {
      console.error("Error updating student batch:", error);
      const message = getFetchErrorMessage(
        error,
        "Failed to update student batch"
      );
      setBatchError(message);
      toast.error(message);
    } finally {
      setSavingBatch(false);
    }
  };

  const handleToggleCourseAccess = async (course) => {
    const courseId = course?.courseId || course?._id;
    if (!studentId || !courseId) return;

    try {
      setUpdatingCourseId(courseId);
      const token = localStorage.getItem("adminToken");
      const response = await axios.put(
        `${API_BASE}/v1/students/admin/course-access/${studentId}/${courseId}`,
        { locked: !Boolean(course.isLocked) },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      toast.success(response.data?.message || "Course access updated");
      if (response.data?.override) {
        onQueueCourseAccessUpdate?.(studentId, courseId, response.data.override);
        onCourseAccessUpdated?.(studentId, courseId, response.data.override);
        setData((current) =>
          current
            ? {
                ...current,
                student: current.student
                  ? mergeStudentCourseAccess(
                      current.student,
                      courseId,
                      response.data.override
                    )
                  : current.student,
                courses: Array.isArray(current.courses)
                  ? current.courses.map((item) => {
                      const itemCourseId = item.courseId || item._id;
                      return String(itemCourseId) === String(courseId)
                        ? mergeCourseAccessUpdate(item, response.data.override)
                        : item;
                    })
                  : current.courses,
              }
            : current
        );
      }
      await refreshOverview({ background: true });
    } catch (error) {
      console.error("Error updating course access:", error);
      toast.error(
        getFetchErrorMessage(error, "Failed to update course access")
      );
    } finally {
      setUpdatingCourseId(null);
    }
  };

  const handleDownloadBookingInvoice = async (bookingId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${API_ORIGIN}/api/v1/course-bookings/admin/${bookingId}/invoice`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Booking-Invoice-${String(bookingId).slice(-8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(
        getFetchErrorMessage(error, "Failed to download booking invoice")
      );
    }
  };

  const handleDownloadReceipt = (receiptId) => {
    const link = `${API_ORIGIN}/api/v1/students/download-receipt/${encodeURIComponent(
      receiptId
    )}`;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 size={20} className="animate-spin text-indigo-500" />
          Loading student details...
        </div>
      </div>
    );
  }

  if (!data?.student) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
        >
          <ArrowLeft size={16} />
          Back to View Students
        </button>
        <p className="text-gray-600 mt-4">
          {errorMessage || "Unable to load student details."}
        </p>
      </div>
    );
  }

  const { courses = [], transactions = [], bookings = [], receipts = [] } = data;
  const overallCompletionPercent = Number(data.overallCompletionPercent || 0);
  const isInactive = String(student.status || "").toLowerCase() === "inactive";
  const currentBatchLabel =
    student.batchCode || student.batchId?.code || "Not assigned";
  const canSaveBatch =
    Boolean(selectedBatchId) &&
    String(selectedBatchId) !== studentBatchId &&
    !batchLoading &&
    !savingBatch;

  return (
    <motion.div
      key="details"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="w-full space-y-6"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
          >
            <ArrowLeft size={16} />
            Back to View Students
          </button>
          <span
            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
              isInactive ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
          >
            {isInactive ? "Inactive" : "Active"}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">Name</p>
            <p className="text-sm font-semibold text-gray-900">{student.name || "N/A"}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">Email</p>
            <p className="text-sm font-semibold text-gray-900">{student.email || "N/A"}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">Phone</p>
            <p className="text-sm font-semibold text-gray-900">{student.phone || "N/A"}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">Location</p>
            <p className="text-sm font-semibold text-gray-900">{student.location || "N/A"}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500">Center</p>
            <p className="text-sm font-semibold text-gray-900">{student.center || "N/A"}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 lg:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs text-emerald-600">Batch Assignment</p>
                <p className="text-sm font-semibold text-emerald-800">
                  {currentBatchLabel}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {studentBatchMode
                    ? `Filtered to ${studentBatchMode} batches`
                    : "Showing all available batches"}
                </p>
              </div>
              <div className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                {batchLoading ? "Loading batches..." : `${batchOptions.length} options`}
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-end">
              <div className="flex-1">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Select batch
                </label>
                <select
                  value={selectedBatchId}
                  onChange={(e) => setSelectedBatchId(e.target.value)}
                  disabled={batchLoading || savingBatch || batchOptions.length === 0}
                  className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <option value="">
                    {batchLoading
                      ? "Loading batch options..."
                      : batchOptions.length === 0
                      ? "No matching batches available"
                      : "Choose a batch"}
                  </option>
                  {batchOptions.map((batch) => {
                    const assignedCount = Number(batch.assignedCount || 0);
                    const size = Number(batch.size || 0);
                    const isFull = size > 0 && assignedCount >= size;
                    const labelParts = [
                      batch.code || batch._id,
                      String(batch.mode || "").toUpperCase(),
                      `${assignedCount}/${size}`,
                    ];

                    return (
                      <option key={batch._id} value={batch._id}>
                        {labelParts.filter(Boolean).join(" | ")}
                        {isFull ? " | Full" : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              <button
                type="button"
                onClick={handleBatchSave}
                disabled={!canSaveBatch}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {savingBatch ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save Batch
              </button>
            </div>

            {batchError ? (
              <p className="mt-3 text-xs font-medium text-rose-600">
                {batchError}
              </p>
            ) : null}
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <p className="text-xs text-indigo-600">Overall Completion</p>
            <p className="text-lg font-bold text-indigo-800">{overallCompletionPercent}%</p>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Joined on {formatDate(student.createdAt)}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Purchased / Enrolled Courses</h3>
        {courses.length === 0 ? (
          <p className="text-sm text-gray-500">No courses found.</p>
        ) : (
          <div className="space-y-3">
            {courses.map((course) => {
              const courseId = course.courseId || course._id;
              const accessState = getCourseAccessState(course);
              const isLocked = accessState.isLocked;
              const statusLabel = accessState.status;
              const isExpired = Boolean(course.isExpired);

              return (
                <div
                  key={courseId}
                  className="rounded-2xl border border-gray-200 bg-gray-50/70 px-4 py-4 shadow-sm"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {course.title || "Untitled Course"}
                        </p>
                        <span className="inline-flex rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
                          {Number(course.completionPercent || 0)}% complete
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-gray-600">
                        <span>
                          Purchased at{" "}
                          <span className="font-semibold text-gray-800">
                            {formatDate(course.purchasedAt)}
                          </span>
                        </span>
                        <span className="hidden sm:inline text-gray-300">|</span>
                        <span>
                          Expiry{" "}
                          <span className="font-semibold text-gray-800">
                            {formatDate(course.expiresAt)}
                          </span>
                        </span>
                        {isExpired && (
                          <>
                            <span className="hidden sm:inline text-gray-300">|</span>
                            <span className="font-semibold text-rose-600">
                              Expired
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          statusLabel === "Active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {statusLabel}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleCourseAccess(course)}
                        disabled={updatingCourseId === courseId}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-colors ${
                          isLocked
                            ? "bg-slate-900 hover:bg-slate-800"
                            : "bg-emerald-600 hover:bg-emerald-700"
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        {updatingCourseId === courseId ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : isLocked ? (
                          <Lock size={16} />
                        ) : (
                          <Unlock size={16} />
                        )}
                        {isLocked ? "Unlock" : "Lock"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Transactions / Receipts</h3>
        {transactions.length === 0 ? (
          <p className="text-sm text-gray-500">No transactions found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Course</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Receipt</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((transaction) => (
                  <tr key={transaction._id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {transaction.courseId?.title || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                      {transaction.sessionType || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{transaction.status || "N/A"}</td>
                    <td className="px-4 py-3 text-sm">
                      {transaction.receiptSent ? "Sent" : "Not Sent"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatDateTime(transaction.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Legacy Receipts</h4>
          {receipts.length === 0 ? (
            <p className="text-sm text-gray-500">No legacy receipts found.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {receipts.map((receipt) => (
                <button
                  key={receipt.id}
                  onClick={() => handleDownloadReceipt(receipt.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold"
                >
                  <Download size={14} />
                  Receipt {receipt.id}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Invoices</h3>
        {bookings.length === 0 ? (
          <p className="text-sm text-gray-500">No bookings found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Paid</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Remaining</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Invoice</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookings.map((booking) => (
                  <tr key={booking._id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{booking.itemTitle || "N/A"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatCurrency(booking.paidAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatCurrency(booking.remainingAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{booking.status || "N/A"}</td>
                    <td className="px-4 py-3 text-sm">
                      {booking.invoiceSent ? "Sent" : "Not Sent"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleDownloadBookingInvoice(booking._id)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                      >
                        <Download size={14} />
                        Download Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function DigitalHubAccessTab({ students, loading, onStudentUpdated }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingStudentId, setUpdatingStudentId] = useState(null);

  const filteredStudents = students.filter((student) =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.phone?.includes(searchTerm) ||
    student.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.center?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleAccess = async (student) => {
    const nextUnlocked = !Boolean(student.digitalHubAccessOverride);
    const actionLabel = nextUnlocked ? "unfreeze" : "restore normal locking for";

    if (
      !window.confirm(
        `Are you sure you want to ${actionLabel} ${student.name}'s Digital Hub access?`
      )
    ) {
      return;
    }

    try {
      setUpdatingStudentId(student._id);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("No admin token found");
      }

      await axios.put(
        `${API_BASE}/v1/students/admin/digital-hub-access/${student._id}`,
        { unlocked: nextUnlocked },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(
        nextUnlocked
          ? `${student.name}'s Digital Hub access is now fully unfrozen.`
          : `${student.name}'s Digital Hub access is back to normal lock order.`
      );

      onStudentUpdated?.();
    } catch (error) {
      console.error("Error updating Digital Hub access:", error);
      toast.error(
        getFetchErrorMessage(error, "Failed to update Digital Hub access")
      );
    } finally {
      setUpdatingStudentId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-500" size={36} />
      </div>
    );
  }

  if (!students?.length) {
    return (
      <div className="text-gray-500 text-center py-12">
        No students available.
      </div>
    );
  }

  if (filteredStudents.length === 0) {
    return (
      <motion.div
        key="access-empty"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
          <User className="mx-auto mb-3 text-gray-300" size={48} />
          <h3 className="text-lg font-semibold text-gray-700">No students found</h3>
          <p className="text-gray-500 mt-1">
            Try a different name, email, phone, location, or center.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="access"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className="w-full"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Digital Hub Access Override
            </h2>
            <p className="text-gray-600 mt-1">
              Unfreeze all chapters and topics for a specific student without affecting others.
            </p>
          </div>
          <div className="relative max-w-md w-full md:w-96">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Use <strong>Unfreeze All</strong> to bypass chapter/topic lock order for one student only.
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Digital Hub Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => {
                const accessEnabled = Boolean(student.digitalHubAccessOverride);
                return (
                  <tr key={student._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0 mr-3">
                          {student.name?.charAt(0)?.toUpperCase() || "S"}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {student.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {student.course && student.course.length > 0 ? (
                          <div className="space-y-1">
                            {student.course.slice(0, 2).map((course, index) => (
                              <div key={index} className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                                {course.title || course}
                              </div>
                            ))}
                            {student.course.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{student.course.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">No courses</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full ${
                        accessEnabled
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-700"
                      }`}>
                        {accessEnabled ? <Unlock size={14} /> : <Lock size={14} />}
                        {accessEnabled ? "Unfrozen" : "Locked by order"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleAccess(student)}
                        disabled={updatingStudentId === student._id}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                          accessEnabled
                            ? "bg-gray-900 hover:bg-gray-800 text-white"
                            : "bg-indigo-500 hover:bg-indigo-600 text-white"
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {updatingStudentId === student._id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : accessEnabled ? (
                          <Lock size={16} />
                        ) : (
                          <Unlock size={16} />
                        )}
                        {accessEnabled ? "Restore Lock Order" : "Unfreeze All"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}

// Student Profile Card Component
function StudentProfileCard({ student, onEdit }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md">
            {student.name?.charAt(0)?.toUpperCase() || 'S'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{student.name}</h3>
            <p className="text-gray-600">{student.email}</p>
          </div>
        </div>
        <button
          onClick={() => onEdit(student)}
          className="bg-indigo-500 hover:bg-indigo-600 text-white p-2 rounded-lg transition-colors flex items-center gap-2"
          title="Edit Profile"
        >
          <Edit size={16} />
          Edit
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3 text-gray-600">
          <Phone size={16} className="text-indigo-500" />
          <span>{student.phone || 'Not provided'}</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600">
          <Monitor size={16} className="text-indigo-500" />
          <span className="capitalize">{student.mode || 'Not specified'}</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600">
          <MapPin size={16} className="text-indigo-500" />
          <span>{student.location || 'Not provided'}</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600">
          <Building size={16} className="text-indigo-500" />
          <span>{student.center || 'Not provided'}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Student ID: {student._id?.slice(-8) || 'N/A'}</span>
          <span>Joined: {new Date(student.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    </motion.div>
  );
}

// Student Profile Management Component
function StudentProfileManagement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingStudent, setEditingStudent] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    mode: "",
    location: "",
    center: "",
    password: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE}/v1/students`
      );
      setStudents(extractStudents(response.data));
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(getFetchErrorMessage(error, "Failed to fetch students"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditStudent = (student) => {
    setEditingStudentId(student._id);
    setEditForm({
      name: student.name || "",
      email: student.email || "",
      phone: student.phone || "",
      mode: student.mode || "",
      location: student.location || "",
      center: student.center || "",
      password: "", // Don't pre-fill password
    });
    setEditError("");
  };

  const handleCancelEdit = () => {
    setEditingStudentId(null);
    setEditForm({
      name: "",
      email: "",
      phone: "",
      mode: "",
      location: "",
      center: "",
      password: "",
    });
    setEditError("");
  };

  const handleEditFormChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
    setEditError("");
  };

  const handleSaveEdit = async (studentId) => {
    setEditLoading(true);
    setEditError("");

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("No admin token found");
      }

      // Prepare update data (exclude empty password)
      const updateData = { ...editForm };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await axios.put(
        `${API_BASE}/v1/students/admin/update-profile/${studentId}`,
        updateData,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      toast.success("Student profile updated successfully!");
      setEditingStudentId(null);
      fetchStudents(); // Refresh the list
    } catch (err) {
      console.error("Error updating student:", err);
      setEditError(getFetchErrorMessage(err, "Something went wrong"));
    } finally {
      setEditLoading(false);
    }
  };

  // Filter students based on search term
  const filteredStudents = students.filter((student) =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.phone?.includes(searchTerm) ||
    student.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.center?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Export to Excel function
  const exportToExcel = () => {
    if (filteredStudents.length === 0) {
      toast.error("No students to export");
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = filteredStudents.map((student, index) => ({
        'S.No': index + 1,
        'Name': student.name || '',
        'Email': student.email || '',
        'Phone': student.phone || '',
        'Status': student.mode || 'Not specified',
        'Location': student.location || '',
        'Center': student.center || '',
        'Student ID': student._id?.slice(-8) || 'N/A',
        'Joined Date': student.createdAt ? new Date(student.createdAt).toLocaleDateString() : '',
        'Courses': student.course && student.course.length > 0 
          ? student.course.map(course => course.title || course).join(', ') 
          : 'No courses',
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 20 },  // Name
        { wch: 30 },  // Email
        { wch: 15 },  // Phone
        { wch: 12 },  // Status
        { wch: 20 },  // Location
        { wch: 20 },  // Center
        { wch: 12 },  // Student ID
        { wch: 15 },  // Joined Date
        { wch: 40 },  // Courses
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Student Profiles');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `student_profiles_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast.success(`${filteredStudents.length} student profiles exported to Excel successfully!`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export student profiles to Excel");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading student profiles...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      key="profile"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="w-full"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Student Profile Management</h2>
          <p className="text-gray-600">Manage and update student profile information</p>
        </div>

        {/* Search and Export Section */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search students by name, email, phone, location, or center..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>
          <Button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <Download size={16} />
            Export Excel
          </Button>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Students</p>
                <p className="text-2xl font-bold text-gray-800">{students.length}</p>
              </div>
              <User className="text-indigo-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Online Students</p>
                <p className="text-2xl font-bold text-green-600">
                  {students.filter(s => s.mode === 'online').length}
                </p>
              </div>
              <Monitor className="text-green-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Offline Students</p>
                <p className="text-2xl font-bold text-blue-600">
                  {students.filter(s => s.mode === 'offline').length}
                </p>
              </div>
              <Building className="text-blue-500" size={32} />
            </div>
          </div>
        </div>

        {/* Excel-style Table */}
        {filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <User className="text-gray-400 mx-auto mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {searchTerm ? 'No students found' : 'No students registered'}
            </h3>
            <p className="text-gray-500">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Students will appear here once they register'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 bg-white border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Center
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Student ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Joined Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Courses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student, index) => (
                  <React.Fragment key={student._id}>
                    {/* Regular row */}
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0 mr-3">
                            {student.name?.charAt(0)?.toUpperCase() || 'S'}
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {student.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          student.mode === 'online' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {student.mode || 'Not specified'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student.location || 'Not provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student.center || 'Not provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student._id?.slice(-8) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r border-gray-300">
                        {new Date(student.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300">
                        {student.course && student.course.length > 0 ? (
                          <div className="space-y-1">
                            {student.course.slice(0, 2).map((course, courseIndex) => (
                              <div key={courseIndex} className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                                {course.title || course}
                              </div>
                            ))}
                            {student.course.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{student.course.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">No courses</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditStudent(student)}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white p-2 rounded transition-colors flex items-center gap-1"
                          title="Edit Profile"
                          disabled={editingStudentId === student._id}
                        >
                          <Edit size={14} />
                          {editingStudentId === student._id ? 'Editing...' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Edit form row */}
                    {editingStudentId === student._id && (
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td colSpan="11" className="px-6 py-6">
                          <div className="bg-white rounded-lg border border-blue-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <Edit size={20} className="text-indigo-500" />
                                Edit Student Profile
                              </h3>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                disabled={editLoading}
                              >
                                <X size={20} />
                              </button>
                            </div>
                            
                            {editError && (
                              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                                <AlertCircle size={16} />
                                {editError}
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                  <input
                                    type="text"
                                    name="name"
                                    value={editForm.name}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                  <input
                                    type="tel"
                                    name="phone"
                                    value={editForm.phone}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                  <input
                                    type="text"
                                    name="location"
                                    value={editForm.location}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                                  <input
                                    type="password"
                                    name="password"
                                    value={editForm.password}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                    placeholder="Leave empty to keep current password"
                                  />
                                </div>
                              </div>
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                  <input
                                    type="email"
                                    name="email"
                                    value={editForm.email}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                  <select
                                    name="mode"
                                    value={editForm.mode}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  >
                                    <option value="">Select Mode</option>
                                    <option value="Digital Hub+Virtual">Digital Hub+Virtual</option>
                                    <option value="Digital Hub+Center">Digital Hub+Center</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Center</label>
                                  <input
                                    type="text"
                                    name="center"
                                    value={editForm.center}
                                    onChange={handleEditFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={editLoading}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-4">
                              <p className="text-sm">
                                <strong>Note:</strong> Profile image cannot be changed through this interface. 
                                Students can update their profile image from their dashboard.
                              </p>
                            </div>

                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                disabled={editLoading}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleSaveEdit(student._id)}
                                className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors flex items-center gap-2"
                                disabled={editLoading}
                              >
                                {editLoading ? (
                                  <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <Save size={16} />
                                    Update Profile
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </motion.div>
  );
}

function StudentDigitalHubPreview({ studentId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [overview, setOverview] = useState(null);
  const [completedTestIds, setCompletedTestIds] = useState([]);
  const [activeSection, setActiveSection] = useState("digital-hub");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!studentId) return;
      setLoading(true);
      setErrorMessage("");
      try {
        const [overviewResult, revisionResult] = await Promise.allSettled([
          axios.get(`${API_BASE}/v1/students/admin/${studentId}/overview`),
          axios.get(
            `${API_BASE}/v1/students/admin/${studentId}/revision-tests-completed`
          ),
        ]);
        if (cancelled) return;

        if (overviewResult.status === "fulfilled") {
          setOverview(overviewResult.value.data);
        } else {
          setErrorMessage(
            getFetchErrorMessage(overviewResult.reason, "Failed to load student data")
          );
        }

        if (revisionResult.status === "fulfilled") {
          setCompletedTestIds(
            Array.isArray(revisionResult.value.data?.completedTestIds)
              ? revisionResult.value.data.completedTestIds
              : []
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const sections = [
    { id: "digital-hub", label: "Digital Hub", icon: <BookOpen size={18} /> },
    { id: "assessment", label: "Assessment", icon: <ClipboardCheck size={18} /> },
    { id: "live-session", label: "Live Session", icon: <Video size={18} /> },
    { id: "live-class", label: "Live Class", icon: <PlayCircle size={18} /> },
    { id: "recorded", label: "Recorded Sessions", icon: <Film size={18} /> },
    { id: "news", label: "News", icon: <Newspaper size={18} /> },
    { id: "testimonial", label: "Testimonial", icon: <Quote size={18} /> },
    { id: "payments", label: "My Payments", icon: <Wallet size={18} /> },
    { id: "bookings", label: "Bookings", icon: <FileText size={18} /> },
    { id: "support", label: "Support", icon: <Headphones size={18} /> },
    { id: "certificates", label: "Certificates", icon: <Award size={18} /> },
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex items-center justify-center h-64"
      >
        <Loader2 className="animate-spin text-indigo-500" size={36} />
      </motion.div>
    );
  }

  if (errorMessage && !overview) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <button
          onClick={onBack}
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          <ArrowLeft size={16} />
          Back to Students
        </button>
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
          {errorMessage}
        </div>
      </motion.div>
    );
  }

  const student = overview?.student || null;
  const courses = Array.isArray(overview?.courses) ? overview.courses : [];
  const bookings = Array.isArray(overview?.bookings) ? overview.bookings : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex flex-col gap-4 lg:flex-row"
    >
      <aside className="shrink-0 rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white p-3 shadow-sm lg:w-64">
        <button
          onClick={onBack}
          className="mb-4 flex w-full items-center gap-2 rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
        >
          <ArrowLeft size={16} />
          Back to Students
        </button>
        <div className="mb-3 px-2">
          <p className="text-sm font-bold text-slate-900">{student?.name || "Student"}</p>
          <p className="truncate text-xs text-slate-500">{student?.email}</p>
          <span className="mt-2 inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            Read-only preview
          </span>
        </div>
        <nav className="space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
                  : "text-slate-700 hover:bg-blue-50"
              }`}
            >
              <span className={activeSection === section.id ? "text-white" : "text-blue-500"}>
                {section.icon}
              </span>
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      <main className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
        {activeSection === "digital-hub" && (
          <CourseTab previewStudentId={studentId} readOnly />
        )}
        {activeSection === "assessment" && (
          <RevisionTab readOnly completedTestIds={completedTestIds} />
        )}
        {activeSection === "live-session" && (
          <LiveClassTab previewStudentId={studentId} readOnly />
        )}
        {activeSection === "live-class" && (
          <LiveClassListTab previewStudentId={studentId} readOnly />
        )}
        {activeSection === "recorded" && (
          <RecordedSessionTab previewStudentId={studentId} readOnly />
        )}
        {activeSection === "news" && <NewsTab />}
        {activeSection === "testimonial" && (
          <TestimonialTab adminStudentId={studentId} readOnly />
        )}
        {activeSection === "payments" && (
          <StudentInvoicesTab
            adminStudentId={studentId}
            readOnly
            title="My Payments"
            description="All payment records, invoices, and transaction references for this student."
          />
        )}
        {activeSection === "bookings" && (
          <StudentBookingsTab presetBookings={bookings} readOnly />
        )}
        {activeSection === "support" && (
          <TicketTab viewerType="admin" filterEmail={student?.email || ""} readOnly />
        )}
        {activeSection === "certificates" && (
          <CertificateTab previewCourses={courses} previewStudent={student} readOnly />
        )}
      </main>
    </motion.div>
  );
}

export default function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");
  const [activeTab, setActiveTab] = useState("add"); // "add", "list", "details", "coursesPreview", "access", or "profile"
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const hasLoadedStudentsRef = useRef(false);
  const pendingCourseAccessOverridesRef = useRef(new Map());

  const refreshStudents = useCallback(async ({ background = false } = {}) => {
    const shouldShowLoader = !background && !hasLoadedStudentsRef.current;
    try {
      if (shouldShowLoader) {
        setLoading(true);
      }
      setStudentsError("");
      const response = await axios.get(`${API_BASE}/v1/students`);
      const reconciled = reconcileStudentsWithPendingCourseAccess(
        extractStudents(response.data),
        pendingCourseAccessOverridesRef.current
      );
      pendingCourseAccessOverridesRef.current = reconciled.pending;
      setStudents(reconciled.students);
      hasLoadedStudentsRef.current = true;
    } catch (err) {
      console.error("Error fetching students:", err);
      const message = getFetchErrorMessage(err, "Failed to fetch students");
      setStudentsError(message);
      toast.error(message);
    } finally {
      if (shouldShowLoader) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (activeTab === "list" || activeTab === "access") {
      refreshStudents();
    }
  }, [activeTab, refreshStudents]);

  const handleStudentAdded = () => {
    setActiveTab("list");
  };

  const handleStudentsUpdated = async () => {
    await refreshStudents({ background: true });
  };

  const queueCourseAccessUpdate = useCallback((studentId, courseId, override) => {
    setPendingCourseAccessOverride(
      pendingCourseAccessOverridesRef.current,
      studentId,
      courseId,
      override
    );
  }, []);

  const applyCourseAccessUpdate = useCallback((studentId, courseId, override) => {
    if (!studentId || !courseId || !override) return;

    setStudents((currentStudents) =>
      currentStudents.map((student) => {
        if (String(student._id) !== String(studentId)) {
          return student;
        }
        return mergeStudentCourseAccess(student, courseId, override);
      })
    );
  }, []);

  const handleViewStudent = (student) => {
    setSelectedStudentId(student?._id || null);
    setActiveTab("details");
  };

  const handlePreviewCourses = (student) => {
    setSelectedStudentId(student?._id || null);
    setActiveTab("coursesPreview");
  };

  const handleGoBack = () => {
    setSelectedStudentId(null);
    setActiveTab("list");
  };

  return (
    <div className="w-full px-4 py-6">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Student Management</h1>
        <p className="text-gray-600">Manage and view all enrolled students</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-8">
        <Button
          onClick={() => setActiveTab("add")}
          className={`flex items-center gap-2 px-6 py-3 ${
            activeTab === "add"
              ? "bg-indigo-500 text-white shadow-lg"
              : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
          }`}
        >
          <PlusCircle size={18} />
          Add Student
        </Button>
        <Button
          onClick={() => setActiveTab("list")}
          className={`flex items-center gap-2 px-6 py-3 ${
            activeTab === "list" || activeTab === "details"
              ? "bg-indigo-500 text-white shadow-lg"
              : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
          }`}
        >
          <ListIcon size={18} />
          View Students
        </Button>
        <Button
          onClick={() => setActiveTab("access")}
          className={`flex items-center gap-2 px-6 py-3 ${
            activeTab === "access"
              ? "bg-indigo-500 text-white shadow-lg"
              : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
          }`}
        >
          <Unlock size={18} />
          Digital Hub Access
        </Button>
        <Button
          onClick={() => setActiveTab("profile")}
          className={`flex items-center gap-2 px-6 py-3 ${
            activeTab === "profile"
              ? "bg-indigo-500 text-white shadow-lg"
              : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
          }`}
        >
          <User size={18} />
          Profile Management
        </Button>
      </div>

      {/* Tab panels */}
      <div className="w-full">
        <AnimatePresence mode="wait">
          {activeTab === "add" && (
            <AddStudentForm key="addform" onSuccess={handleStudentAdded} />
          )}
          {activeTab === "list" &&
            (loading ? (
              <motion.div
                key="loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center h-64"
              >
                <Loader2 className="animate-spin text-indigo-500" size={36} />
              </motion.div>
            ) : (
              <StudentsTable
                key="studentstable"
                students={students}
                onStudentUpdated={handleStudentsUpdated}
                onViewStudent={handleViewStudent}
                onPreviewCourses={handlePreviewCourses}
                onCourseAccessUpdated={applyCourseAccessUpdate}
                onQueueCourseAccessUpdate={queueCourseAccessUpdate}
                errorMessage={studentsError}
              />
            ))}
          {activeTab === "details" && selectedStudentId && (
            <StudentDetailsView
              key="studentdetailsview"
              studentId={selectedStudentId}
              onBack={handleGoBack}
              onCourseAccessUpdated={applyCourseAccessUpdate}
              onQueueCourseAccessUpdate={queueCourseAccessUpdate}
              pendingCourseAccessOverridesRef={pendingCourseAccessOverridesRef}
            />
          )}
          {activeTab === "coursesPreview" && selectedStudentId && (
            <StudentDigitalHubPreview
              key="studentdigitalhubpreview"
              studentId={selectedStudentId}
              onBack={handleGoBack}
            />
          )}
          {activeTab === "access" &&
            (loading ? (
              <motion.div
                key="access-loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center h-64"
              >
                <Loader2 className="animate-spin text-indigo-500" size={36} />
              </motion.div>
            ) : (
              <DigitalHubAccessTab
                key="digitalhubaccess"
                students={students}
                loading={loading}
                onStudentUpdated={handleStudentsUpdated}
              />
            ))}
          {activeTab === "profile" && (
            <StudentProfileManagement key="profilemanagement" />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
